import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar, MapPin, Users, DollarSign, Trash2, CreditCard, Loader2, Camera, Edit, UserPlus, X, Archive, Link2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { getImageUrl } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EventDetailPage({ user }) {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [allMembers, setAllMembers] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [addingAttendee, setAddingAttendee] = useState(false);

  useEffect(() => {
    fetchEvent();
    if (user.is_admin || user.is_area_manager) {
      fetchMembers();
    }
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'success') {
      toast.success('Payment successful! Your booking is confirmed.');
    } else if (paymentStatus === 'cancelled') {
      toast.error('Payment was cancelled.');
    } else if (paymentStatus === 'error') {
      toast.error('Payment failed. Please try again.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}`);
      setEvent(response.data);
    } catch (error) {
      toast.error('Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  const handleBook = async (payOnline = false) => {
    try {
      const response = await axios.post(`${API}/events/${eventId}/book?pay_online=${payOnline}`);
      
      if (response.data.requires_payment) {
        // Initiate payment flow
        setProcessingPayment(true);
        try {
          const paymentResponse = await axios.post(`${API}/payments/create`, {
            event_id: eventId
          });
          
          // Redirect to Ozow payment page
          window.location.href = paymentResponse.data.payment_url;
        } catch (paymentError) {
          toast.error('Failed to initiate payment');
          setProcessingPayment(false);
        }
      } else {
        fetchEvent();
        if (response.data.pay_later) {
          toast.success('Booking confirmed! Please pay R100 at the meeting.');
        } else {
          toast.success('Booking confirmed!');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Booking failed');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this event permanently? Use Archive instead to keep attendee data.')) return;

    try {
      await axios.delete(`${API}/events/${eventId}`);
      toast.success('Event deleted');
      navigate('/events');
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleArchive = async () => {
    if (!window.confirm('Archive this event? It will be hidden from the events page but all attendee data is preserved.')) return;
    try {
      await axios.patch(`${API}/events/${eventId}`, { archived: true });
      toast.success('Event archived');
      navigate('/events');
    } catch (error) {
      toast.error('Failed to archive event');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      // First upload the image
      const uploadResponse = await axios.post(`${API}/upload/event-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Then update the event with the new image URL
      await axios.patch(`${API}/events/${eventId}`, {
        image: uploadResponse.data.image_url
      });
      
      // Refresh the event data
      fetchEvent();
      toast.success('Event image updated!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setAllMembers(response.data.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '')));
    } catch (error) {
      console.error('Failed to fetch members:', error);
    }
  };

  const handleAddAttendee = async () => {
    if (!selectedMemberId) return;
    setAddingAttendee(true);
    try {
      await axios.post(`${API}/events/${eventId}/add-attendee`, { user_id: selectedMemberId });
      toast.success('Member added to event');
      setSelectedMemberId('');
      fetchEvent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    } finally {
      setAddingAttendee(false);
    }
  };

  const handleRemoveAttendee = async (userId) => {
    try {
      await axios.delete(`${API}/events/${eventId}/remove-attendee/${userId}`);
      toast.success('Member removed from event');
      fetchEvent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };


  const openEditModal = () => {
    // Pre-fill form with current event data
    setEditForm({
      title: event.title,
      description: event.description,
      location: event.location,
      date: event.date ? event.date.slice(0, 16) : '',
      end_time: event.end_time || '',
      is_paid: event.is_paid,
      price: event.price || '',
      max_attendees: event.max_attendees || ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const updateData = {
        title: editForm.title,
        description: editForm.description,
        location: editForm.location,
        date: editForm.date,
        end_time: editForm.end_time || null,
        is_paid: editForm.is_paid,
        price: editForm.is_paid && editForm.price ? parseFloat(editForm.price) : null,
        max_attendees: editForm.max_attendees ? parseInt(editForm.max_attendees) : null
      };
      
      await axios.patch(`${API}/events/${eventId}`, updateData);
      fetchEvent();
      setShowEditModal(false);
      toast.success('Event updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-12">Loading...</div>;
  if (!event) return <div className="text-center py-12">Event not found</div>;

  const isBooked = event.attendees.includes(user.id);
  const isFull = event.max_attendees && event.attendees.length >= event.max_attendees;
  const canDelete = event.user_id === user.id || user.is_admin || user.is_area_manager;
  const canEdit = user.is_admin || user.is_area_manager;
  const isCreator = event.user_id === user.id;
  const isLeaderOrAdmin = user.is_admin || user.is_area_manager;
  const isChapterMeeting = !!event.chapter;
  const eventPrice = event.price || 0;

  return (
    <div className="max-w-4xl mx-auto px-6" data-testid="event-detail-page">
      <Card className="border-gray-100 rounded-2xl shadow-lg overflow-hidden">
        {/* Event Image at Top - only admin/chapter leaders can edit */}
        <div className="relative aspect-[3/1] sm:aspect-[3.5/1] overflow-hidden bg-gradient-to-br from-[#5b7f95] to-[#4a6b80]">
          {event.image && (
            <img 
              src={getImageUrl(event.image)}
              alt={event.title}
              className="w-full h-full object-cover"
              style={{ objectPosition: event.image_position || 'center' }}
            />
          )}
          {canEdit && (
            <label className="absolute bottom-4 right-4 cursor-pointer">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/90 hover:bg-white rounded-full shadow-lg transition-colors">
                <Camera className="w-4 h-4 text-[#5b7f95]" />
                <span className="text-sm font-medium text-[#5b7f95]">
                  {uploadingImage ? 'Uploading...' : (event.image ? 'Change' : 'Add Image')}
                </span>
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                disabled={uploadingImage}
              />
            </label>
          )}
        </div>
        <CardContent className="p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-4xl font-bold text-[#2d3748] mb-2">
                {event.title}
              </h1>
              <p className="text-[#64748b]">Organized by {event.user_name}</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {/* Copy Event Link - for leaders/admins to share on WhatsApp */}
              {(canEdit || isCreator) && (
                <Button
                  variant="outline"
                  onClick={() => {
                    const url = `${window.location.origin}/events/${eventId}`;
                    navigator.clipboard.writeText(url);
                    toast.success('Event link copied! Share it on WhatsApp.');
                  }}
                  className="rounded-full border-[#cb9944] text-[#cb9944] hover:bg-[#cb9944] hover:text-white"
                  data-testid="copy-event-link-btn"
                >
                  <Link2 className="w-4 h-4 mr-2" />
                  Copy Link
                </Button>
              )}
              {canEdit && (
                <Button
                  variant="outline"
                  onClick={openEditModal}
                  className="rounded-full border-[#5b7f95] text-[#5b7f95] hover:bg-[#5b7f95] hover:text-white"
                  data-testid="edit-event-btn"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              )}
              {canDelete && (
                <>
                  <Button
                    variant="outline"
                    onClick={handleArchive}
                    className="rounded-full border-[#cb9944] text-[#cb9944] hover:bg-[#cb9944]/10"
                    data-testid="archive-event-btn"
                  >
                    <Archive className="w-4 h-4 mr-2" />
                    Archive
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleDelete}
                    className="rounded-full"
                    data-testid="delete-event-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="space-y-4 mb-8">
            <div className="flex items-center gap-3 text-[#64748b]">
              <Calendar className="w-5 h-5 text-[#5b7f95]" />
              <span>
                {format(new Date(event.date), 'PPPP p')}
                {event.end_time && ` — ${event.end_time}`}
              </span>
            </div>
            <div className="flex items-center gap-3 text-[#64748b]">
              <MapPin className="w-5 h-5 text-[#5b7f95]" />
              <span>{event.location}</span>
            </div>
            <div className="flex items-center gap-3 text-[#64748b]">
              <Users className="w-5 h-5 text-[#5b7f95]" />
              <span>
                {event.attendees.length} attending
                {event.max_attendees && ` / ${event.max_attendees} max`}
                {isFull && <span className="ml-2 text-red-500 font-semibold">(FULL)</span>}
              </span>
            </div>
            {event.is_paid && (
              <div className="flex items-center gap-3 font-semibold text-lg">
                <CreditCard className="w-5 h-5 text-[#cb9944]" />
                {isChapterMeeting && (isCreator || isLeaderOrAdmin) ? (
                  <span className="text-[#cb9944]">
                    R100.00
                    <span className="text-sm font-normal text-[#5b7f95] ml-2">Pay at meeting or online</span>
                  </span>
                ) : isChapterMeeting ? (
                  <span className="text-[#cb9944]">
                    R250.00
                    <span className="text-sm font-normal text-[#64748b] ml-2">- Pay via Ozow</span>
                  </span>
                ) : isCreator ? (
                  <span className="text-green-600">Free (Event Creator)</span>
                ) : (
                  <span className="text-[#cb9944]">
                    R{eventPrice.toFixed(2)}
                    <span className="text-sm font-normal text-[#64748b] ml-2">- Pay via Ozow</span>
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-[#2d3748] mb-4">
              About This Event
            </h2>
            <p className="text-[#64748b] whitespace-pre-wrap leading-relaxed">{event.description}</p>
          </div>

          {/* Attendee List */}
          <div className="mb-8" data-testid="attendee-list">
            <h2 className="text-2xl font-semibold text-[#2d3748] mb-4">
              Attending ({event.attendees?.length || 0}{event.max_attendees ? `/${event.max_attendees}` : ''})
            </h2>
            
            {/* Add Attendee - Admin/Leader only */}
            {canEdit && (
              <div className="flex items-center gap-3 mb-4" data-testid="add-attendee-section">
                <Select value={selectedMemberId} onValueChange={setSelectedMemberId}>
                  <SelectTrigger className="flex-1 rounded-full" data-testid="add-attendee-select">
                    <SelectValue placeholder="Select a member to add..." />
                  </SelectTrigger>
                  <SelectContent>
                    {allMembers
                      .filter(m => !event.attendees?.includes(m.id))
                      .map(m => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.full_name} — {m.area}
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
                <Button
                  onClick={handleAddAttendee}
                  disabled={!selectedMemberId || addingAttendee}
                  className="bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-full"
                  data-testid="add-attendee-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add
                </Button>
              </div>
            )}

            {event.attendee_details && event.attendee_details.length > 0 ? (
              <div className="flex flex-wrap gap-3">
                {event.attendee_details.map((attendee) => (
                  <div 
                    key={attendee.id} 
                    className="flex items-center gap-2 bg-[#f0f5f8] rounded-full px-4 py-2"
                    data-testid="attendee-chip"
                  >
                    {attendee.profile_image ? (
                      <img 
                        src={getImageUrl(attendee.profile_image)} 
                        alt={attendee.display_name || attendee.full_name}
                        className="w-7 h-7 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-[#5b7f95] flex items-center justify-center text-white text-xs font-semibold">
                        {(attendee.display_name || attendee.full_name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="text-sm font-medium text-[#2d3748]">
                      {attendee.display_name || attendee.full_name}
                    </span>
                    {canEdit && (
                      <button
                        onClick={() => handleRemoveAttendee(attendee.id)}
                        className="ml-1 w-5 h-5 rounded-full bg-red-100 hover:bg-red-200 text-red-500 flex items-center justify-center transition-colors"
                        title="Remove attendee"
                        data-testid="remove-attendee-btn"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#64748b] text-sm">No attendees yet</p>
            )}
          </div>

          {!isBooked ? (
            isChapterMeeting && (isCreator || isLeaderOrAdmin) && event.is_paid && !isFull ? (
              <div className="flex flex-col gap-3" data-testid="chapter-meeting-booking-options">
                <Button
                  onClick={() => handleBook(false)}
                  disabled={processingPayment}
                  className="w-full bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-full py-6 text-lg"
                  data-testid="book-pay-later-btn"
                >
                  Book Now (Pay R100 at the meeting)
                </Button>
                <Button
                  onClick={() => handleBook(true)}
                  disabled={processingPayment}
                  variant="outline"
                  className="w-full border-[#cb9944] text-[#cb9944] hover:bg-[#cb9944] hover:text-white rounded-full py-6 text-lg"
                  data-testid="book-pay-online-btn"
                >
                  {processingPayment ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Pay R100.00 Online via Ozow
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => handleBook(false)}
                disabled={isFull || processingPayment}
                className="w-full bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-full py-6 text-lg"
                data-testid="book-event-btn"
              >
                {processingPayment ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : isFull ? (
                  'Event Full'
                ) : !event.is_paid ? (
                  'Book Now - Free'
                ) : isChapterMeeting ? (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Pay R250.00 & Book via Ozow
                  </>
                ) : isCreator ? (
                  'Book Now - Free'
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Pay R{eventPrice.toFixed(2)} & Book via Ozow
                  </>
                )}
              </Button>
            )
          ) : (
            <div className="w-full bg-green-50 border border-green-200 text-green-700 text-center py-6 rounded-full font-semibold" data-testid="booked-indicator">
              ✓ You're attending this event
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Event Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#2d3748]">Edit Event</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="edit-title">Event Title</Label>
              <Input
                id="edit-title"
                value={editForm.title || ''}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                className="bg-gray-50 border-gray-200 rounded-xl"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={editForm.description || ''}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                className="bg-gray-50 border-gray-200 rounded-xl min-h-[100px]"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="edit-date">Start Date & Time</Label>
              <Input
                id="edit-date"
                type="datetime-local"
                value={editForm.date || ''}
                onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                className="bg-gray-50 border-gray-200 rounded-xl"
                required
              />
            </div>

            <div>
              <Label htmlFor="edit-end-time">End Time (optional, e.g. 14:00)</Label>
              <Input
                id="edit-end-time"
                type="time"
                value={editForm.end_time || ''}
                onChange={(e) => setEditForm({ ...editForm, end_time: e.target.value })}
                className="bg-gray-50 border-gray-200 rounded-xl"
              />
            </div>
            
            <div>
              <Label htmlFor="edit-location">Location</Label>
              <Input
                id="edit-location"
                value={editForm.location || ''}
                onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                className="bg-gray-50 border-gray-200 rounded-xl"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="edit-max-attendees">Max Attendees (optional)</Label>
              <Input
                id="edit-max-attendees"
                type="number"
                value={editForm.max_attendees || ''}
                onChange={(e) => setEditForm({ ...editForm, max_attendees: e.target.value })}
                className="bg-gray-50 border-gray-200 rounded-xl"
                placeholder="Leave empty for unlimited"
              />
            </div>
            
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="edit-is-paid"
                checked={editForm.is_paid || false}
                onChange={(e) => setEditForm({ ...editForm, is_paid: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300"
              />
              <Label htmlFor="edit-is-paid" className="cursor-pointer">This is a paid event</Label>
            </div>
            
            {editForm.is_paid && (
              <div>
                <Label htmlFor="edit-price">Price (R)</Label>
                <Input
                  id="edit-price"
                  type="number"
                  step="0.01"
                  value={editForm.price || ''}
                  onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                  className="bg-gray-50 border-gray-200 rounded-xl"
                  required={editForm.is_paid}
                />
              </div>
            )}
            
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditModal(false)}
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="flex-1 bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-full"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
